import React, { useState } from 'react';
import PropTypes from 'prop-types';

import Form from 'Components/molecules/Form/Form';
import Input from 'Components/atoms/Input/Input';
import Button from 'Components/atoms/Button/Button';
import Label from 'Components/molecules/Label/Label';

import { withPolaris } from 'Components/protons/Polaris';

import './_style.scss';

const NewsletterSignUpForm = ({
  context: { cssPrefix },
  extraClassNames,
  newsletterOptions,
  title,
  onSubmit,
  terms: {
    privacyPolicyUrl,
    disclaimer,
  },
}) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedNewsletters, setSelectedNewsletters] = useState([]);
  const [isUnsubscribed, setIsUnsubscribed] = useState(false);

  const handleInputChange = (setState) => (event) => setState(event.target.value);

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    setSelectedNewsletters((prevSelected) => (checked ? [...prevSelected, name] : prevSelected.filter((item) => item !== name)));
  };

  const handleUnsubscribeChange = (event) => setIsUnsubscribed(event.target.checked);

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit({
      firstName,
      lastName,
      email,
      selectedNewsletters,
      isUnsubscribed,
    });
  };

  return (
    <Form
      onSubmit={handleSubmit}
      extraClassNames={{
        [`${cssPrefix}__newsletter-sign-up-form`]: true,
        ...extraClassNames,
      }}
    >
      <h2 className={`${cssPrefix}__newsletter-sign-up-form--title`}>{title}</h2>
      <div className={`${cssPrefix}__newsletter-sign-up-form--user-info`}>
        <div className={`${cssPrefix}__newsletter-sign-up-form--user-info--name-wrapper`}>
          <div className={`${cssPrefix}__newsletter-sign-up-form--user-info--first-name`}>
            <Label
              content="First name*"
              target={`${cssPrefix}__newsletter-sign-up-form--first-name`}
            />
            <Input
              extraClassNames={{ [`${cssPrefix}__newsletter-sign-up-form--input`]: true }}
              name={`${cssPrefix}__newsletter-sign-up-form--first-name`}
              id={`${cssPrefix}__newsletter-sign-up-form--first-name`}
              placeholder="John"
              required
              type="text"
              onChange={handleInputChange(setFirstName)}
            />
          </div>
          <div className={`${cssPrefix}__newsletter-sign-up-form--user-info--last-name`}>
            <Label
              content="Last name*"
              target={`${cssPrefix}__newsletter-sign-up-form--last-name`}
            />
            <Input
              extraClassNames={{ [`${cssPrefix}__newsletter-sign-up-form--input`]: true }}
              name={`${cssPrefix}__newsletter-sign-up-form--last-name`}
              id={`${cssPrefix}__newsletter-sign-up-form--last-name`}
              placeholder="Smith"
              required
              type="text"
              onChange={handleInputChange(setLastName)}
            />
          </div>
        </div>
        <div className={`${cssPrefix}__newsletter-sign-up-form--user-info--email`}>
          <Label
            content="Email address*"
            target={`${cssPrefix}__newsletter-sign-up-form--email`}
          />
          <Input
            extraClassNames={{ [`${cssPrefix}__newsletter-sign-up-form--input`]: true }}
            name={`${cssPrefix}__newsletter-sign-up-form--email`}
            id={`${cssPrefix}__newsletter-sign-up-form--email`}
            placeholder="john.smith@example.com"
            required
            type="email"
            onChange={handleInputChange(setEmail)}
          />
        </div>
      </div>

      <div className={`${cssPrefix}__newsletter-sign-up-form--additional-newsletters`}>
        <p className={`${cssPrefix}__newsletter-sign-up-form--additional-newsletters--title`}>
          I would also like to receive the following newsletters:
        </p>
        {Object.keys(newsletterOptions)
          .filter((key) => newsletterOptions[key].enabled)
          .map((key) => {
            const { label, frequency } = newsletterOptions[key];
            return (
              <Label
                key={key}
                content=""
                extraClassNames={{ [`${cssPrefix}__newsletter-sign-up-form--additional-newsletters--option`]: true }}
                target={`${cssPrefix}__newsletter-sign-up-form--${key}`}
              >
                <Input
                  id={`${cssPrefix}__newsletter-sign-up-form--${key}`}
                  name={key}
                  type="checkbox"
                  checked={selectedNewsletters.includes(key)}
                  onChange={handleCheckboxChange}
                />
                {label}
                {' '}
                <span>{frequency}</span>
              </Label>
            );
          })}
      </div>

      <div className={`${cssPrefix}__newsletter-sign-up-form--terms`}>
        <p className={`${cssPrefix}__newsletter-sign-up-form--terms--description`}>
          You agree to the processing, storage, sharing and use of this information for
          the purpose of managing your newsletter subscription as described in our
          {' '}
          <a target="_blank" rel="noopener noreferrer" href={privacyPolicyUrl}>Privacy Policy</a>
          .
        </p>
        <p className={`${cssPrefix}__newsletter-sign-up-form--terms--description`}>
          We believe that based on your newsletter subscription you may be interested in other related products and services we offer.
          As described in our Privacy Policy we will use the information you have shared to send you communications about such products and services.
          If you do not wish to receive these communications from us then please
          <Label
            content=""
            target={`${cssPrefix}__newsletter-sign-up-form--unsubscribe`}
            extraClassNames={{ [`${cssPrefix}__newsletter-sign-up-form--terms--unsubscribe`]: true }}
          >
            {' '}
            click here
            {' '}
            <Input
              id={`${cssPrefix}__newsletter-sign-up-form--unsubscribe`}
              name={`${cssPrefix}__newsletter-sign-up-form--unsubscribe`}
              type="checkbox"
              onChange={handleUnsubscribeChange}
            />
          </Label>
          to be unsubscribed from this activity.
        </p>
        <p className={`${cssPrefix}__newsletter-sign-up-form--terms--description`}>
          {disclaimer}
        </p>
      </div>
      <Button
        type="submit"
        extraClassNames={{ [`${cssPrefix}__newsletter-sign-up-form--button`]: true }}
      >
        Sign Up
      </Button>
    </Form>
  );
};

const newsletterOptionShape = PropTypes.shape({
  name: PropTypes.string,
  label: PropTypes.string,
  frequency: PropTypes.string,
  enabled: PropTypes.bool,
});

NewsletterSignUpForm.propTypes = {
  context: PropTypes.shape({
    cssPrefix: PropTypes.string,
  }),
  extraClassNames: PropTypes.shape({}),
  title: PropTypes.string,
  newsletterOptions: PropTypes.shape({
    auto_express: newsletterOptionShape,
    auto_express_video: newsletterOptionShape,
    carbuyer: newsletterOptionShape,
    evo: newsletterOptionShape,
    driving_electric: newsletterOptionShape,
  }),
  onSubmit: PropTypes.func,
  terms: PropTypes.shape({
    privacyPolicyUrl: PropTypes.string,
    disclaimer: PropTypes.string,
  }),
};

NewsletterSignUpForm.defaultProps = {
  context: {},
  extraClassNames: {},
  title: 'We need a few details',
  newsletterOptions: {
    auto_express: {
      label: 'Auto Express newsletter',
      frequency: '(weekly)',
      enabled: true,
    },
    auto_express_video: {
      label: 'Auto Express video newsletter',
      frequency: '(weekly)',
      enabled: true,
    },
    carbuyer: {
      label: 'CarBuyer newsletter',
      frequency: '(daily)',
      enabled: true,
    },
    evo: {
      label: 'Evo newsletter',
      frequency: '(three a week)',
      enabled: true,
    },
    driving_electric: {
      label: 'Driving Electric newsletter',
      frequency: '(daily)',
      enabled: false,
    },
  },
  terms: {
    privacyPolicyUrl: 'https://www.carwowgroup.com/policies/privacy-policy/',
    disclaimer: '‘We’ includes AutoExpress and other Carwow Studio brands: Carbuyer, Driver Power, Driving Electric and Evo.',
  },
  onSubmit: () => {},
};

export default withPolaris(NewsletterSignUpForm);
